import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { DEFAULT_OG_IMAGE, NOT_FOUND_SEO, ROUTE_SEO, RouteSeo, SITE_NAME, SITE_URL } from '../seo-config';

@Injectable({ providedIn: 'root' })
export class MetadataService {
    constructor(
        private title: Title,
        private meta: Meta,
        @Inject(DOCUMENT) private doc: Document
    ) {}

    updateForRoute(url: string): void {
        const path = this.normalizePath(url);
        const seo = ROUTE_SEO[path] ?? NOT_FOUND_SEO;
        const absoluteUrl = `${SITE_URL}${path === '/' ? '/' : path}`;
        this.apply(seo, absoluteUrl);
    }

    private apply(seo: RouteSeo, absoluteUrl: string): void {
        this.title.setTitle(seo.title);
        this.meta.updateTag({
            name: 'robots',
            content: seo.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'
        });

        this.meta.updateTag({ name: 'description', content: seo.description });
        if (seo.keywords) {
            this.meta.updateTag({ name: 'keywords', content: seo.keywords });
        }

        this.meta.updateTag({ property: 'og:title', content: seo.title });
        this.meta.updateTag({ property: 'og:description', content: seo.description });
        this.meta.updateTag({ property: 'og:url', content: absoluteUrl });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
        this.meta.updateTag({ property: 'og:image', content: DEFAULT_OG_IMAGE });

        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: seo.title });
        this.meta.updateTag({ name: 'twitter:description', content: seo.description });
        this.meta.updateTag({ name: 'twitter:image', content: DEFAULT_OG_IMAGE });

        this.setCanonical(absoluteUrl);
    }

    private setCanonical(url: string): void {
        let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!link) {
            link = this.doc.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.doc.head.appendChild(link);
        }
        link.setAttribute('href', url);
    }

    private normalizePath(url: string): string {
        const noFragment = url.split('#')[0];
        const noQuery = noFragment.split('?')[0];
        if (!noQuery || noQuery === '/') {
            return '/';
        }
        return noQuery.endsWith('/') && noQuery.length > 1
            ? noQuery.slice(0, -1)
            : noQuery;
    }
}
